import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { stockComparisonService, StockComparisonTrendPoint } from '../../../../core/modules/stockComparison/services/api';

interface Props {
  companyId: number;
}

// Un punto por dia con carga -- si un dia no tuvo carga, el backend
// directamente no manda ese punto (no se rellena con 0%, seria enganoso
// mostrar "0% de desviacion" un dia que nadie subio el archivo).
const StockComparisonTrendChart = ({ companyId }: Props) => {
  const theme = useTheme();
  const [points, setPoints] = useState<StockComparisonTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    stockComparisonService
      .getTrend(companyId, 90)
      .then((data) => setPoints(data.points))
      .catch(() => setError('No se pudo cargar la evolución de desviación.'))
      .finally(() => setLoading(false));
  }, [companyId]);

  const labels = useMemo(() => points.map((p) => moment(p.comparisonDate).format('DD/MM/YYYY')), [points]);

  const series = useMemo(
    () => [
      {
        name: '% desviación promedio',
        data: points.map((p) => (p.avgDeviationPct === null ? null : Number(p.avgDeviationPct))),
      },
      {
        name: '% desviación máxima',
        data: points.map((p) => (p.maxDeviationPct === null ? null : Number(p.maxDeviationPct))),
      },
    ],
    [points],
  );

  const chartOptions: any = {
    chart: {
      fontFamily: 'inherit',
      foreColor: 'inherit',
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: ['#1E88E5', '#E53935'],
    labels,
    dataLabels: { enabled: false },
    grid: { borderColor: theme.palette.divider },
    legend: { show: true, position: 'top' },
    stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 4] },
    tooltip: { theme: theme.palette.mode },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { color: theme.palette.divider },
      labels: {
        style: { colors: theme.palette.text.secondary, fontSize: '10px' },
        rotate: -45,
        hideOverlappingLabels: true,
        maxHeight: 80,
      },
      tickAmount: Math.min(12, points.length),
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (value: number) => `${Math.round(value)}%`,
      },
      title: { text: '% de desviación' },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (points.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Todavía no hay cargas registradas para armar la evolución. En cuanto subas al menos un
        archivo, cada día que cargues va a sumar un punto acá.
      </Typography>
    );
  }

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="line"
      height={340}
    />
  );
};

export default StockComparisonTrendChart;
